import React from 'react'
import { Avatar, Box, type IColumn, layoutStore, Typography } from 'shared/ui'
import { EmptyCell } from 'shared/ui/Table'
import { getLabelAsNumberInternationalFormat } from 'shared/lib'
import { type CallHistory } from 'entities/CallHistory/model/CallHistory'
import { AiAgentUserCell } from 'entities/AICalling/ui/columns/AiAgentUserCell'
import { CellDuration } from 'pages/conversations/pages/calls/ui/CallHistoryTable/ui/CellDuration/CellDuration'
import { CellOutcome } from 'pages/conversations/pages/calls/ui/CallHistoryTable/ui/CellOutcome/CellOutcome'
import { CellContact } from 'pages/conversations/pages/calls/ui/CallHistoryTable/ui/CellContact/CellContact'
import { CellTime } from 'pages/conversations/pages/calls/ui/CallHistoryTable/ui/CellTime/CellTime'
import { CellAudio } from 'pages/conversations/pages/calls/ui/CallHistoryTable/ui/CellAudio/CellAudio'
import { type CallHistoryStore } from 'pages/conversations/pages/calls/ui/CallHistory'
import { CellInbox } from 'pages/conversations/pages/calls/ui/CallHistoryTable/ui/CellInbox/CellInbox'
import styles from './styles.module.scss'

export const callHistoryColumns = {
  contact: (config?: Partial<IColumn<CallHistory>>): IColumn<CallHistory> => {
    return {
      field: 'contact',
      name: 'Contact',
      width: '100%',
      maxWidth: '362px',
      isDisabledChangeVisible: true,
      disableLink: true,
      renderRowCell: (row, column, config) => (
        <CellContact info={row} showActions={config.isHover} withCalls />
      ),
      ...config,
    }
  },
  number: (config?: Partial<IColumn<CallHistory>>): IColumn<CallHistory> => ({
    field: 'number',
    name: 'Phone',
    isDisabledChangeVisible: true,
    disableLink: true,
    renderRowCell: (row) => row.number && getLabelAsNumberInternationalFormat(row.number),
    ...config,
  }),
  outcome: (config?: Partial<IColumn<CallHistory>>): IColumn<CallHistory> => ({
    field: 'outcome',
    name: 'Outcome',
    isDisabledChangeVisible: true,
    disableLink: true,
    renderRowCell: (row) => {
      return (
        <CellOutcome
          type={row.callType}
          isForwarded={row.isForwarded}
          isConferenceCall={!!row.call_conference?.participants?.length}
          isOutboundCall={row.isOutboundCall}
          isInboundCall={row.isInboundCall}
          label={row.outcomeType}
          agentCallId={row.agentCallId}
        />
      )
    },
    ...config,
  }),
  record_source: (config?: Partial<IColumn<CallHistory>>): IColumn<CallHistory> => ({
    field: 'record_source',
    name: 'Recording type',
    isHideByDefault: true,
    disableLink: true,
    renderRowCell: (row) => {
      if (!row.call) {
        return <EmptyCell />
      }

      return (
        <Box display={'flex'} gap={'6px'}>
          <div className={styles.recordSource}>{row.call.record_source}</div>
        </Box>
      )
    },
    ...config,
  }),
  duration: (config?: Partial<IColumn<CallHistory>>): IColumn<CallHistory> => ({
    field: 'duration',
    name: 'Duration',
    isHideByDefault: true,
    disableLink: true,
    renderRowCell: (row) => {
      return <CellDuration duration={row.callDuration} />
    },
    ...config,
  }),
  audio: (
    callHistoryStore: CallHistoryStore,
    config?: Partial<IColumn<CallHistory>>
  ): IColumn<CallHistory> => ({
    field: 'audio',
    name: 'Audio',
    isHideByDefault: true,
    isOverflowInitial: true,
    disableLink: true,
    renderRowCell: (row, column, config) => {
      if (row.audioUrl) {
        return (
          <CellAudio
            url={row.audioUrl}
            isUnread={row.isUnread}
            callItem={row}
            tableConfig={config}
            callHistoryStore={callHistoryStore}
          />
        )
      }

      return <EmptyCell />
    },
    ...config,
  }),
  created_at: (config?: Partial<IColumn<CallHistory>>): IColumn<CallHistory> => ({
    field: 'created_at',
    name: 'Date',
    minWidth: 164,
    isHideByDefault: true,
    disableLink: true,
    isSortable: true,
    renderRowCell: (row) => {
      return <CellTime info={row} />
    },
    ...config,
  }),
  user: (config?: Partial<IColumn<CallHistory>>): IColumn<CallHistory> => ({
    field: 'user',
    name: 'User',
    isHideByDefault: true,
    tooltipQuestionProps: {
      placement: 'top',
      label: (
        <>
          Agent who made or received the call;
          <br />
          inbox owner for missed calls.
        </>
      ),
    },
    disableLink: true,
    renderRowCell: (row) => {
      if (row.agentCallId) return <AiAgentUserCell />
      if (!row.contacted_user) {
        return <EmptyCell />
      }

      return (
        <Box display={'flex'} gap={'6px'}>
          {row.avatarInfo && <Avatar info={row.avatarInfo} size={16} />}

          <Typography
            variant={'body-md-regular'}
            ellipsis
            tooltipProps={{ label: row.contactedUserName }}
            ariaLabel={'name'}
          >
            <div className={styles.userWrap}>
              <div className={styles.user}>{row.contactedUserName}</div>
            </div>
          </Typography>
        </Box>
      )
    },
    ...config,
  }),
  inbox: (config?: Partial<IColumn<CallHistory>>): IColumn<CallHistory> => ({
    field: 'inbox',
    name: 'Inbox',
    isHideByDefault: true,
    disableLink: true,
    renderRowCell: (row) => {
      if (!row.inbox) {
        return <EmptyCell />
      }
      return <CellInbox name={row.inboxName} number={row.inboxNumber} />
    },
    ...config,
  }),
}

export const getColumns = (
  columnIds: string[],
  callHistoryStore: CallHistoryStore
): IColumn<CallHistory>[] => {
  const hasLastColumn = !(
    columnIds?.includes('created_at') ||
    columnIds?.includes('user') ||
    columnIds?.includes('inbox')
  )

  const { isViewXXsGlobal } = layoutStore

  return [
    callHistoryColumns.contact({ minWidth: isViewXXsGlobal ? 180 : 192 }),
    callHistoryColumns.number({ minWidth: 128 }),
    callHistoryColumns.outcome({ minWidth: 160 }),
    callHistoryColumns.record_source(),
    callHistoryColumns.duration({ minWidth: 110 }),
    callHistoryColumns.audio(callHistoryStore, {
      minWidth: hasLastColumn ? 260 : 94,
    }),
    callHistoryColumns.created_at(),
    callHistoryColumns.user(),
    callHistoryColumns.inbox(),
  ]
}
